import { authorizedFetch } from './httpClient';

const API_URL = process.env.REACT_APP_API_URL;

export type PaymentMethod = 'TEST_PAYMENT';

export interface PayOffLiabilitiesRequest {
  liabilities: string[];
  paymentMethod: PaymentMethod;
}

export interface PayOffLiabilitiesSuccess {
  paymentId: string;
}

export interface PayOffLiabilitiesError {
  errorCode: 'LIABILITIES_NOT_FOUND' | 'LIABILITY_NOT_PENDING' | 'PAYMENT_METHOD_REQUIRED' | 'ANY_LIABILITY_REQUIRED';
  message: string;
  missingLiabilities?: string[];
  notPendingLiabilities?: string[];
}

// Convenience envelope built client-side by branching on response.ok — mirrors childrenApi.ts::createChild.
// The HTTP body itself is only ever PayOffLiabilitiesSuccess or PayOffLiabilitiesError, never a wrapper.
export interface PayOffLiabilitiesResponse {
  isSuccess: boolean;
  success: PayOffLiabilitiesSuccess | null;
  error: PayOffLiabilitiesError | null;
}

export const payOffLiabilities = async (request: PayOffLiabilitiesRequest): Promise<PayOffLiabilitiesResponse> => {
  const response = await authorizedFetch(`${API_URL}/api/payments`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
  if (response.ok) {
    const success = (await response.json()) as PayOffLiabilitiesSuccess;
    return { isSuccess: true, success, error: null };
  }
  const error = (await response.json()) as PayOffLiabilitiesError;
  return { isSuccess: false, success: null, error };
};

export interface UserInfo {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
}

export interface PaymentEntry {
  id: string;
  paymentMethod: PaymentMethod;
  amount: number;
  paymentDate: string;
  createdBy: UserInfo;
}

export interface PaginatedResponse<T> {
  rows: T[];
  totalElements: number;
  totalPages: number;
  page: number;
  size: number;
}

export interface PaymentFilters {
  page?: number;
  size?: number;
  sortOrder?: 'ASC' | 'DESC';
}

export const getPayments = async (filters: PaymentFilters): Promise<PaginatedResponse<PaymentEntry>> => {
  const params = new URLSearchParams();
  if (filters.page !== undefined) params.append('pagination.page', String(filters.page));
  if (filters.size !== undefined) params.append('pagination.pageSize', String(filters.size));
  if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

  const response = await authorizedFetch(`${API_URL}/api/payments?${params.toString()}`);
  if (!response.ok) throw new Error('Failed to fetch payments');
  return response.json();
};
